import { useMemo, useState } from "react";
import { Info, CheckCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { completeAuthSetup } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";

const EMOJI_OPTIONS = [
  "🦁", "🐯", "🐺", "🦊", "🐻", "🐼",
  "🦅", "🦋", "🦜", "🐬", "🐉", "🦄",
  "⚡", "🌊", "🔥", "🌙", "⭐", "🎯",
  "🚀", "🌿", "💎", "🏔️", "🌺", "🎸",
];

const MONTH_OPTIONS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const COUNTRY_OPTIONS = ["+91", "+1", "+44"];

const INITIALS_PALETTES = [
  { bg: "#E0F2FE", text: "#0C4A6E", border: "#BAE6FD" },
  { bg: "#ECFEFF", text: "#155E75", border: "#A5F3FC" },
  { bg: "#ECFDF3", text: "#166534", border: "#BBF7D0" },
  { bg: "#FEF9C3", text: "#854D0E", border: "#FDE68A" },
  { bg: "#FFF7ED", text: "#9A3412", border: "#FDBA74" },
  { bg: "#FCE7F3", text: "#9D174D", border: "#F9A8D4" },
  { bg: "#EEF2FF", text: "#3730A3", border: "#C7D2FE" },
  { bg: "#F5F3FF", text: "#6D28D9", border: "#DDD6FE" },
  { bg: "#F3F4F6", text: "#374151", border: "#D1D5DB" },
  { bg: "#FFE4E6", text: "#9F1239", border: "#FDA4AF" },
  { bg: "#FEF3C7", text: "#92400E", border: "#FCD34D" },
  { bg: "#E2E8F0", text: "#1E293B", border: "#CBD5E1" },
];

const DEFAULT_PREFS = {
  task_assigned: true,
  task_overdue: true,
  mention: true,
  leave_approved: true,
  weekly_digest: false,
};

const splitName = (name = "") => {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  return {
    first: parts[0] || "",
    last: parts.slice(1).join(" ") || "",
  };
};

const initialsFromName = (firstName = "", lastName = "") => {
  const first = String(firstName || "").trim().charAt(0);
  const last = String(lastName || "").trim().charAt(0);
  return `${first}${last}`.toUpperCase() || "AS";
};

const deterministicPaletteIndex = (firstName = "", lastName = "") => {
  const seed = `${firstName}${lastName}`.trim().toUpperCase();
  if (!seed) return 0;
  const first = seed.charCodeAt(0) || 0;
  const second = seed.charCodeAt(1) || first;
  return (first + second) % INITIALS_PALETTES.length;
};

const parseBirthday = (value) => {
  if (!value || !String(value).includes("-")) return { day: "", month: "", year: "" };
  const [year, month, day] = String(value).split("-");
  return { day, month, year };
};

const setupPreferenceRows = [
  {
    key: "task_assigned",
    title: "Task assigned to me",
    subtitle: "When someone assigns you a task",
  },
  {
    key: "task_overdue",
    title: "Task overdue",
    subtitle: "Daily digest of your overdue tasks",
  },
  {
    key: "mention",
    title: "@mention in notes",
    subtitle: "When someone tags you in a task comment",
  },
  {
    key: "leave_approved",
    title: "Leave request approved",
    subtitle: "When your manager acts on a leave request",
  },
  {
    key: "weekly_digest",
    title: "Weekly health summary",
    subtitle: "Every Monday — project health digest",
  },
];

export default function AuthSetupPage({ user, onAuthSuccess }) {
  const navigate = useNavigate();
  const parsedName = splitName(user?.name || "");
  const parsedBirthday = parseBirthday(user?.birthday || "");

  const [step, setStep] = useState(1);
  const [firstName, setFirstName] = useState(user?.first_name || parsedName.first || "");
  const [lastName, setLastName] = useState(user?.last_name || parsedName.last || "");
  const [birthdayDay, setBirthdayDay] = useState(parsedBirthday.day || "");
  const [birthdayMonth, setBirthdayMonth] = useState(parsedBirthday.month || "");
  const [birthdayYear, setBirthdayYear] = useState(parsedBirthday.year || "");
  const [avatarMode, setAvatarMode] = useState(user?.avatar_mode || (user?.avatar_emoji ? "emoji" : (user?.picture ? "picture" : "initials")));
  const [selectedEmoji, setSelectedEmoji] = useState(user?.avatar_emoji || "🦁");
  const [selectedPicture] = useState(user?.picture || "");
  const [selectedInitialsPalette, setSelectedInitialsPalette] = useState(
    deterministicPaletteIndex(user?.first_name || parsedName.first || "", user?.last_name || parsedName.last || ""),
  );
  const [notificationPrefs, setNotificationPrefs] = useState({
    ...DEFAULT_PREFS,
    ...(user?.notification_prefs || {}),
  });
  const [whatsappCountryCode, setWhatsappCountryCode] = useState(user?.whatsapp_country_code || "+91");
  const [whatsappNumber, setWhatsappNumber] = useState(user?.whatsapp_number || "");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const initials = useMemo(() => initialsFromName(firstName, lastName), [firstName, lastName]);
  const activeInitialsPalette = INITIALS_PALETTES[selectedInitialsPalette] || INITIALS_PALETTES[0];

  const validateStepOne = () => {
    if (!firstName.trim() || !lastName.trim()) {
      toast.error("First name and last name are required.");
      return false;
    }
    if (!birthdayDay || !birthdayMonth) {
      toast.error("Please complete birthday day and month.");
      return false;
    }
    return true;
  };

  const handleContinue = () => {
    if (step === 1 && !validateStepOne()) return;
    setStep((prev) => Math.min(3, prev + 1));
  };

  const handleBack = () => {
    setStep((prev) => Math.max(1, prev - 1));
  };

  const handleTogglePref = (key) => {
    setNotificationPrefs((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleFinish = async () => {
    if (!validateStepOne()) {
      setStep(1);
      return;
    }

    setIsSubmitting(true);
    try {
      const session = await completeAuthSetup({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        birthday_day: Number(birthdayDay),
        birthday_month: Number(birthdayMonth),
        birthday_year: birthdayYear ? Number(birthdayYear) : null,
        avatar_mode: avatarMode,
        avatar: avatarMode === "emoji" ? selectedEmoji : null,
        notification_prefs: notificationPrefs,
        whatsapp_country_code: whatsappCountryCode,
        whatsapp_number: whatsappNumber,
      });
      onAuthSuccess(session);
      toast.success("Profile setup complete");
      navigate("/dashboard", { replace: true });
    } catch (error) {
      toast.error(error.message || "Unable to save setup. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#f3f3f4] px-4 py-7" data-testid="auth-setup-page-root">
      <div className="mx-auto max-w-[470px]" data-testid="auth-setup-page-container">
        <header className="mb-4 text-center" data-testid="auth-setup-header">
          <h1 className="text-[44px] leading-none font-semibold tracking-tight text-slate-900" data-testid="auth-setup-brand-name">
            Dimerse
          </h1>
          <p className="mt-1 text-xs text-slate-400" data-testid="auth-setup-brand-tagline">Work Digitally · Connect Ahead</p>
        </header>

        <section className="overflow-hidden rounded-[22px] border border-slate-200 bg-white shadow-sm" data-testid="auth-setup-card">
          <div className="h-1 w-full bg-slate-100" data-testid="auth-setup-progress-track">
            <div
              className="h-full rounded-r-full bg-indigo-600 transition-all"
              style={{ width: `${(step / 3) * 100}%` }}
              data-testid="auth-setup-progress-fill"
            />
          </div>

          <div className="px-6 py-6" data-testid="auth-setup-content-wrap">
            {step === 1 ? (
              <div data-testid="auth-setup-step-1">
                <p className="text-xs font-semibold uppercase tracking-[0.1em] text-indigo-600" data-testid="auth-setup-step-label">Step 1 of 3</p>
                <h2 className="mt-2 text-[44px] leading-[1.05] font-semibold text-slate-900" data-testid="auth-setup-step-title">
                  Let's set up your profile
                </h2>
                <p className="mt-2 text-base text-slate-500" data-testid="auth-setup-step-subtitle">This only takes a minute. You won't see this screen again.</p>

                <div className="mt-6" data-testid="auth-setup-signed-in-wrap">
                  <p className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500" data-testid="auth-setup-signed-in-label">Signed in as</p>
                  <div className="mt-2 inline-flex items-center gap-3 rounded-full border border-slate-200 bg-slate-50 px-3 py-2" data-testid="auth-setup-signed-in-chip">
                    <span className="grid h-8 w-8 place-items-center rounded-full bg-emerald-500 text-xs font-bold text-white" data-testid="auth-setup-signed-in-initials">
                      {initials || "A"}
                    </span>
                    <span className="text-base font-medium text-slate-800" data-testid="auth-setup-signed-in-email">{user.email}</span>
                    <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                  </div>
                </div>

                {user.role === "viewer" ? (
                  <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="auth-setup-viewer-warning-card">
                    <p className="text-sm font-semibold text-amber-900" data-testid="auth-setup-viewer-warning-title">
                      You've been added with Viewer access.
                    </p>
                    <p className="mt-1 text-sm text-amber-800" data-testid="auth-setup-viewer-warning-copy">
                      You currently have read-only access until an admin assigns your role. Your admin has been notified.
                    </p>
                  </div>
                ) : (
                  <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-800" data-testid="auth-setup-role-assigned-badge">
                    Role assigned: {user.role.replace(/_/g, " ")}
                  </div>
                )}

                <div className="mt-6 grid gap-3 md:grid-cols-2" data-testid="auth-setup-name-grid">
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500" htmlFor="setup-first-name" data-testid="auth-setup-first-name-label">First Name *</label>
                    <Input id="setup-first-name" className="mt-2 h-12 rounded-xl text-base" value={firstName} onChange={(event) => setFirstName(event.target.value)} data-testid="auth-setup-first-name-input" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500" htmlFor="setup-last-name" data-testid="auth-setup-last-name-label">Last Name *</label>
                    <Input id="setup-last-name" className="mt-2 h-12 rounded-xl text-base" value={lastName} onChange={(event) => setLastName(event.target.value)} data-testid="auth-setup-last-name-input" />
                  </div>
                </div>

                <div className="mt-5" data-testid="auth-setup-birthday-wrap">
                  <p className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500" data-testid="auth-setup-birthday-label">
                    Birthday * <span className="normal-case tracking-normal text-slate-400">Day and month are required for birthday leave</span>
                  </p>
                  <div className="mt-2 grid gap-2 md:grid-cols-3" data-testid="auth-setup-birthday-grid">
                    <select className="h-12 rounded-xl border border-slate-200 px-3 text-base text-slate-800" value={birthdayDay} onChange={(event) => setBirthdayDay(event.target.value)} data-testid="auth-setup-birthday-day-select">
                      <option value="">Day</option>
                      {Array.from({ length: 31 }, (_, index) => String(index + 1).padStart(2, "0")).map((day) => (
                        <option key={day} value={day}>{Number(day)}</option>
                      ))}
                    </select>

                    <select className="h-12 rounded-xl border border-slate-200 px-3 text-base text-slate-800" value={birthdayMonth} onChange={(event) => setBirthdayMonth(event.target.value)} data-testid="auth-setup-birthday-month-select">
                      <option value="">Month</option>
                      {MONTH_OPTIONS.map((month, index) => (
                        <option key={month} value={String(index + 1).padStart(2, "0")}>{month}</option>
                      ))}
                    </select>

                    <select className="h-12 rounded-xl border border-slate-200 px-3 text-base text-slate-800" value={birthdayYear} onChange={(event) => setBirthdayYear(event.target.value)} data-testid="auth-setup-birthday-year-select">
                      <option value="">Year</option>
                      {Array.from({ length: 56 }, (_, index) => 2005 - index).map((year) => (
                        <option key={year} value={String(year)}>{year}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mt-3 grid gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600 md:grid-cols-2" data-testid="auth-setup-birthday-note-card">
                  <div className="flex gap-3">
                    <Info className="mt-0.5 h-4 w-4 text-slate-400" />
                    <p data-testid="auth-setup-birthday-note-copy-1">Your birthday is used to assign your annual <strong>Birthday Leave</strong>.</p>
                  </div>
                  <p data-testid="auth-setup-birthday-note-copy-2"><strong>1 paid day off</strong> on your birthday. Only HR and Super Admins can see this.</p>
                </div>

                <Button type="button" className="mt-5 h-12 w-full rounded-xl bg-slate-400 text-lg font-semibold" onClick={handleContinue} data-testid="auth-setup-step-1-continue-button">
                  Continue →
                </Button>
              </div>
            ) : null}

            {step === 2 ? (
              <div data-testid="auth-setup-step-2">
                <p className="text-xs font-semibold uppercase tracking-[0.1em] text-indigo-600" data-testid="auth-setup-step-2-label">Step 2 of 3</p>
                <h2 className="mt-2 text-[40px] leading-[1.05] font-semibold text-slate-900" data-testid="auth-setup-step-2-title">
                  Pick your avatar
                </h2>
                <p className="mt-2 text-base text-slate-500" data-testid="auth-setup-step-2-subtitle">This shows next to your name across the app.</p>

                <div className="mt-5 flex items-center gap-3 rounded-xl border border-slate-200 p-3" data-testid="auth-setup-avatar-preview-card">
                  <div
                    className="grid h-14 w-14 place-items-center rounded-2xl text-2xl font-semibold"
                    style={
                      avatarMode === "emoji"
                        ? { background: "linear-gradient(145deg,#4f46e5,#312e81)", color: "#fff" }
                        : avatarMode === "picture"
                          ? { background: "#f8fafc", border: "1px solid #e2e8f0", overflow: "hidden" }
                          : { background: activeInitialsPalette.bg, color: activeInitialsPalette.text, border: `1px solid ${activeInitialsPalette.border}` }
                    }
                    data-testid="auth-setup-avatar-preview-icon"
                  >
                    {avatarMode === "picture" && selectedPicture ? (
                      <img src={selectedPicture} alt={user.name || user.email} className="h-full w-full object-cover" data-testid="auth-setup-avatar-preview-image" />
                    ) : avatarMode === "emoji" ? selectedEmoji : initials}
                  </div>
                  <div>
                    <p className="text-xl font-semibold text-slate-900" data-testid="auth-setup-avatar-preview-name">{firstName || "First"} {lastName || "Last"}</p>
                    <p className="text-sm text-slate-500" data-testid="auth-setup-avatar-preview-role">{user.role.replace(/_/g, " ")} · {user.email.split("@")[1]}</p>
                  </div>
                </div>

                {selectedPicture ? (
                  <div className="mt-5 flex items-center gap-2" data-testid="auth-setup-google-picture-row">
                    <button
                      type="button"
                      onClick={() => setAvatarMode("picture")}
                      className={`h-11 rounded-xl border px-4 text-sm font-semibold transition ${
                        avatarMode === "picture" ? "border-indigo-500 bg-indigo-50 text-indigo-700" : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                      }`}
                      data-testid="auth-setup-use-google-picture-button"
                    >
                      Use Google profile photo
                    </button>
                    <span className="text-xs text-slate-500" data-testid="auth-setup-google-picture-note">You can upload a custom photo later in Profile.</span>
                  </div>
                ) : null}

                <p className="mt-5 text-xs font-semibold uppercase tracking-[0.1em] text-slate-500" data-testid="auth-setup-avatar-picker-label">Choose an avatar</p>
                <div className="mt-2 grid grid-cols-4 gap-2" data-testid="auth-setup-emoji-grid">
                  {EMOJI_OPTIONS.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      onClick={() => {
                        setAvatarMode("emoji");
                        setSelectedEmoji(emoji);
                      }}
                      className={`h-14 rounded-xl border text-2xl transition ${
                        avatarMode === "emoji" && selectedEmoji === emoji
                          ? "border-indigo-500 bg-indigo-50"
                          : "border-slate-200 bg-white hover:border-slate-300"
                      }`}
                      data-testid={`auth-setup-emoji-option-${emoji}`}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>

                <p className="mt-5 text-xs font-semibold uppercase tracking-[0.1em] text-slate-500" data-testid="auth-setup-initials-picker-label">Or use your initials</p>
                <div className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-6" data-testid="auth-setup-initials-grid">
                  {INITIALS_PALETTES.slice(0, 6).map((palette, index) => (
                    <button
                      key={`initial-${index}`}
                      type="button"
                      onClick={() => {
                        setAvatarMode("initials");
                        setSelectedInitialsPalette(index);
                      }}
                      className="h-28 rounded-[22px] border text-[44px] font-semibold transition"
                      style={{
                        background: palette.bg,
                        color: palette.text,
                        borderColor: avatarMode === "initials" && selectedInitialsPalette === index ? "#4f46e5" : palette.border,
                        boxShadow: avatarMode === "initials" && selectedInitialsPalette === index ? "0 0 0 2px rgba(79,70,229,.20)" : "none",
                      }}
                      data-testid={`auth-setup-initials-option-${index + 1}`}
                    >
                      {initials || "AS"}
                    </button>
                  ))}
                </div>

                <div className="mt-6 flex flex-wrap gap-2" data-testid="auth-setup-step-2-actions">
                  <Button type="button" variant="outline" className="h-11 min-w-28 rounded-xl text-sm" onClick={handleBack} data-testid="auth-setup-step-2-back-button">
                    ← Back
                  </Button>
                  <Button type="button" className="h-11 flex-1 rounded-xl bg-slate-400 text-lg font-semibold" onClick={handleContinue} data-testid="auth-setup-step-2-continue-button">
                    Continue →
                  </Button>
                </div>
              </div>
            ) : null}

            {step === 3 ? (
              <div data-testid="auth-setup-step-3">
                <p className="text-xs font-semibold uppercase tracking-[0.1em] text-indigo-600" data-testid="auth-setup-step-3-label">Step 3 of 3</p>
                <h2 className="mt-2 text-[40px] leading-[1.05] font-semibold text-slate-900" data-testid="auth-setup-step-3-title">
                  Almost there
                </h2>
                <p className="mt-2 text-base text-slate-500" data-testid="auth-setup-step-3-subtitle">Quick preferences — you can change these any time in Settings.</p>

                <div className="mt-7 overflow-hidden rounded-2xl border border-slate-200" data-testid="auth-setup-notification-card">
                  {setupPreferenceRows.map((row) => (
                    <div key={row.key} className="flex items-center justify-between border-b border-slate-200 px-3 py-3 last:border-b-0" data-testid={`auth-setup-pref-row-${row.key}`}>
                      <div>
                        <p className="text-sm font-medium text-slate-900" data-testid={`auth-setup-pref-title-${row.key}`}>{row.title}</p>
                        <p className="text-xs text-slate-500" data-testid={`auth-setup-pref-subtitle-${row.key}`}>{row.subtitle}</p>
                      </div>
                      <button
                        type="button"
                        className={`relative h-9 w-16 rounded-full transition ${notificationPrefs[row.key] ? "bg-emerald-500" : "bg-slate-200"}`}
                        onClick={() => handleTogglePref(row.key)}
                        data-testid={`auth-setup-pref-toggle-${row.key}`}
                      >
                        <span className={`absolute top-1 h-7 w-7 rounded-full bg-white transition ${notificationPrefs[row.key] ? "left-8" : "left-1"}`} />
                      </button>
                    </div>
                  ))}
                </div>

                <div className="mt-6" data-testid="auth-setup-whatsapp-wrap">
                  <p className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500" data-testid="auth-setup-whatsapp-label">
                    WhatsApp Number <span className="normal-case tracking-normal text-slate-400">Optional — for task alerts</span>
                  </p>
                  <div className="mt-2 grid gap-2 md:grid-cols-[120px_1fr]" data-testid="auth-setup-whatsapp-fields-row">
                    <select className="h-11 rounded-xl border border-slate-200 px-3 text-base" value={whatsappCountryCode} onChange={(event) => setWhatsappCountryCode(event.target.value)} data-testid="auth-setup-whatsapp-country-select">
                      {COUNTRY_OPTIONS.map((code) => (
                        <option key={code} value={code}>{code}</option>
                      ))}
                    </select>
                    <Input className="h-11 rounded-xl text-base" value={whatsappNumber} onChange={(event) => setWhatsappNumber(event.target.value)} placeholder="98765 43210" data-testid="auth-setup-whatsapp-number-input" />
                  </div>
                  <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-500" data-testid="auth-setup-whatsapp-note">
                    Used only for overdue task alerts and urgent project notifications. We won't spam you.
                  </div>
                </div>

                <div className="mt-6 flex flex-wrap gap-2" data-testid="auth-setup-step-3-actions">
                  <Button type="button" variant="outline" className="h-11 min-w-28 rounded-xl text-sm" onClick={handleBack} data-testid="auth-setup-step-3-back-button">
                    ← Back
                  </Button>
                  <Button
                    type="button"
                    className="h-11 flex-1 rounded-xl bg-slate-900 text-base font-semibold"
                    onClick={handleFinish}
                    disabled={isSubmitting}
                    data-testid="auth-setup-finish-button"
                  >
                    {isSubmitting ? "Saving..." : "Let's get to work →"}
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        </section>

        <p className="mt-4 text-center text-xs text-slate-400" data-testid="auth-setup-footer-note">
          Your data stays within your workspace · Only admins can see birthday and contact info
        </p>
      </div>
    </main>
  );
}
